// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { sparseAssetSchema } from '@/asset/api/types';
import { AssetAssignmentStatus } from '@/constants/asset-assignment-status';
import { zodCatch } from '@/utilities/zod-catch';
import { z } from 'zod';

const PATH = '/v1/sparse/assets';

/**
 * Schema for the sparse asset listing response.
 * Matches the backend sparse.AssetListing structure.
 */
const sparseAssetListingSchema = z.object({
  results: z.array(sparseAssetSchema).catch(zodCatch([])),
  totalCount: z.number(),
});

const responseSchema = z
  .object({
    data: sparseAssetListingSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({
    ...response,
    assets: data.results,
    totalCount: data.totalCount,
  }));

export interface GetSparseAssetsOptions {
  /** Search term to filter by serial number, product name, or SKU */
  query?: string;
  /** Maximum number of results to return */
  limit?: number;
  /** Filter by scrapped status (default: false for non-scrapped) */
  scrapped?: boolean;
  /** Filter by project assignment status (only 'none' is currently supported) */
  withProjectStatus?: AssetAssignmentStatus[];
}

/**
 * Requests sparse assets from the remote API.
 * This endpoint returns a lightweight list of assets for autocomplete/dropdown use cases.
 */
export async function getSparseAssets(options: GetSparseAssetsOptions = {}) {
  const params = new URLSearchParams();

  // Add search query if provided
  if (options.query) {
    params.append('query', options.query);
  }

  // Add limit if provided or use safe default
  const limit = options.limit ?? 200;
  params.append('limit', limit.toString());

  // Add scrapped filter - backend expects 'scrapped' param
  const scrapped = options.scrapped ?? false;
  params.append('scrapped', scrapped ? 'true' : 'false');

  // Add project status filter if provided
  if (options.withProjectStatus) {
    for (const status of options.withProjectStatus) {
      params.append('withProjectStatus', status);
    }
  }

  const response = await api.get(PATH, { params });
  const result = await parseResponse(responseSchema, response.data, {
    schemaName: 'SparseAssets',
    endpoint: PATH,
  });
  return result;
}
